type HeaderSource =
    | Headers
    | {
          get?: (name: string) => string | null | undefined;
          [key: string]: unknown;
      }
    | null
    | undefined;

export const parseRetryAfterSeconds = (retryAfterHeader: unknown): number | null => {
    if (typeof retryAfterHeader === "number" && Number.isFinite(retryAfterHeader) && retryAfterHeader > 0) {
        return Math.ceil(retryAfterHeader);
    }

    if (typeof retryAfterHeader !== "string") {
        return null;
    }

    const trimmedValue = retryAfterHeader.trim();
    if (!trimmedValue) {
        return null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue) && numericValue > 0) {
        return Math.ceil(numericValue);
    }

    const parsedDate = Date.parse(trimmedValue);
    if (Number.isFinite(parsedDate)) {
        const deltaMs = parsedDate - Date.now();
        if (deltaMs > 0) {
            return Math.ceil(deltaMs / 1000);
        }
    }

    return null;
};

const getHeaderValue = (headers: HeaderSource, name: string): unknown => {
    if (!headers) {
        return null;
    }

    if (typeof (headers as { get?: unknown }).get === "function") {
        const headerSourceWithGetter = headers as {
            get: (headerName: string) => string | null | undefined;
        };
        return headerSourceWithGetter.get(name) ?? headerSourceWithGetter.get(name.toLowerCase());
    }

    const record = headers as Record<string, unknown>;
    return record[name] ?? record[name.toLowerCase()] ?? record[name.toUpperCase()] ?? null;
};

export const getRetryAfterSecondsFromHeaders = (headers: HeaderSource): number | null =>
    parseRetryAfterSeconds(getHeaderValue(headers, "Retry-After"));

export const getRetryAfterSecondsFromPayload = (payload: unknown): number | null => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    const record = payload as Record<string, unknown>;
    return (
        parseRetryAfterSeconds(record.retry_after_seconds) ??
        parseRetryAfterSeconds(record.retryAfterSeconds) ??
        parseRetryAfterSeconds(record.retry_after) ??
        parseRetryAfterSeconds(record.retryAfter)
    );
};

export const getRateLimitMessage = (
    payload: unknown,
    fallbackMessage = "Too many requests. Please wait a moment and try again."
): string => {
    if (typeof payload === "string" && payload.trim()) {
        return payload.trim();
    }

    if (!payload || typeof payload !== "object") {
        return fallbackMessage;
    }

    const record = payload as Record<string, unknown>;

    if (typeof record.message === "string" && record.message.trim()) {
        return record.message.trim();
    }

    if (typeof record.error_description === "string" && record.error_description.trim()) {
        return record.error_description.trim();
    }

    if (typeof record.error === "string" && record.error.trim()) {
        return record.error.trim();
    }

    if (typeof record.detail === "string" && record.detail.trim()) {
        return record.detail.trim();
    }

    if (record.detail && typeof record.detail === "object") {
        const detailRecord = record.detail as Record<string, unknown>;
        if (typeof detailRecord.message === "string" && detailRecord.message.trim()) {
            return detailRecord.message.trim();
        }
    }

    return fallbackMessage;
};

export const buildRetryMessage = (baseMessage: string, retryAfterSeconds: number | null): string => {
    if (!retryAfterSeconds || retryAfterSeconds <= 0) {
        return baseMessage;
    }
    return `${baseMessage} Try again in ${retryAfterSeconds} seconds.`;
};

const RATE_LIMIT_COOLDOWN_PREFIX = "qit:rate-limit-cooldown:";

export const getRateLimitCooldownUntil = (key: string): number => {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(`${RATE_LIMIT_COOLDOWN_PREFIX}${key}`);
    const value = Number(raw || 0);
    return Number.isFinite(value) ? value : 0;
};

export const isRateLimitCoolingDown = (key: string, now = Date.now()): boolean =>
    getRateLimitCooldownUntil(key) > now;

export const setRateLimitCooldown = (
    key: string,
    retryAfterSeconds: number | null,
    fallbackSeconds = 60
): number => {
    const safeSeconds = Math.max(1, retryAfterSeconds || fallbackSeconds);
    const until = Date.now() + safeSeconds * 1000;
    if (typeof window !== "undefined") {
        window.localStorage.setItem(`${RATE_LIMIT_COOLDOWN_PREFIX}${key}`, String(until));
    }
    return until;
};
